import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { atsDesign, atsEdit, atsTailor, resumeFileUrl } from '../../lib/api';
import type { AtsDesign, AtsRecord, AtsResumeDoc } from '../../lib/api';
import CoachPanel from './CoachPanel';
import ResumePaper, { LOOK } from './ResumePaper';
import { EMPTY_JOB, JobFields, jobProblem } from './NewResume';
import type { AtsJob, JobDraft } from './NewResume';
import { Btn, C, Modal, ModelChip, ScoreRing } from './ui';

// Step ③ Your resume: the resume as its PDF looks (click a line to edit,
// "Show changes" marks the AI's words) next to the AI Coach. The toolbar
// picks the design, downloads the files, and makes a copy for another job.

const TailorModal: React.FC<{
  open: boolean; onClose: () => void; rec: AtsRecord; pw: string; jobs: AtsJob[]; onOpen: (id: string) => void;
}> = ({ open, onClose, rec, pw, jobs, onOpen }) => {
  const [job, setJob] = useState<JobDraft>(EMPTY_JOB);
  const [sending, setSending] = useState(false);
  const start = async () => {
    const why = jobProblem(job);
    if (why) { toast.error(why); return; }
    setSending(true);
    try {
      const res = await atsTailor(pw, rec._id, { jobTitle: job.jobTitle.trim(), company: job.company.trim(), jd: job.jd.trim(), jobId: job.jobId || undefined });
      onClose();
      setJob(EMPTY_JOB);
      onOpen(res.id);
      toast.success('Checking the same resume for the new job');
    } catch (err: any) { toast.error(err.message); }
    finally { setSending(false); }
  };
  return (
    <Modal open={open} onClose={onClose} wide title="↪ Use this resume for another job"
      sub={`${rec.candidateName}'s original resume is checked again for the new job. This one (${rec.jobTitle}${rec.company ? ` · ${rec.company}` : ''}) stays as it is.`}
      footer={
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-gray-500 flex-1">No need to upload the resume again. Facts you gave in the chat come along.</p>
          <Btn kind="gold" size="lg" onClick={start} disabled={sending}>{sending ? 'Starting…' : 'Check for the new job →'}</Btn>
        </div>
      }>
      <JobFields jobs={jobs} value={job} onChange={setJob} />
    </Modal>
  );
};

const Workspace: React.FC<{
  rec: AtsRecord; pw: string; jobs: AtsJob[];
  onChanged: (r?: AtsRecord) => void; onOpen: (id: string) => void; onRechoose: () => void; onCompare: () => void;
}> = ({ rec, pw, jobs, onChanged, onOpen, onRechoose, onCompare }) => {
  const [draft, setDraftState] = useState<AtsResumeDoc>(rec.resume!);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showChanges, setShowChanges] = useState(true);
  const [design, setDesign] = useState<AtsDesign>(rec.design);
  const [pane, setPane] = useState<'resume' | 'coach'>('resume');
  const [menu, setMenu] = useState(false);
  const [tailoring, setTailoring] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const aiBusy = rec.busy === 'chat';
  const openTips = rec.boosters.filter(b => b.status === 'open').length;

  // The AI's changes replace the draft unless the admin is mid-edit
  useEffect(() => { if (!dirty) setDraftState(rec.resume!); }, [rec.resume, dirty]);
  useEffect(() => { setDesign(rec.design); }, [rec.design]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const setDraft = (f: (d: AtsResumeDoc) => AtsResumeDoc) => { setDraftState(d => f(d)); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...draft, skills: draft.skills.map(g => ({ ...g, items: g.items.map(s => s.trim()).filter(Boolean) })) };
      const res = await atsEdit(pw, rec._id, clean);
      setDirty(false);
      onChanged(res.resume);
      toast.success('Saved');
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const discard = () => { setDraftState(rec.resume!); setDirty(false); };
  const pickDesign = async (d: AtsDesign) => {
    setDesign(d);
    try { const res = await atsDesign(pw, rec._id, d); onChanged(res.resume); }
    catch (err: any) { toast.error(err.message); setDesign(rec.design); }
  };

  const menuItem = 'w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-purple-50 hover:text-purple-900 flex items-center gap-2';
  return (
    <div className="space-y-4 ats-fade">
      {/* Toolbar */}
      <div className="bg-white rounded-3xl border border-gray-100 ats-card p-4 sm:p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xl font-black text-gray-900 truncate">{rec.candidateName || 'Candidate'}</p>
            <p className="text-sm text-gray-600 truncate">{rec.jobTitle}{rec.company ? ` · ${rec.company}` : ''}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <ModelChip id={rec.models.make} prefix="Written by " />
              {rec.tailoredFrom && <button onClick={() => onOpen(rec.tailoredFrom)} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-50 text-gray-600 border border-gray-200 hover:border-purple-300">↪ Made from another job</button>}
              {rec.compare && <button onClick={onCompare} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:border-amber-400">⚖ Compared {rec.compare.models.length} models</button>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center"><ScoreRing value={rec.scoreBefore} size={58} stroke={6} muted /><p className="text-[10px] font-bold text-gray-400 mt-1">BEFORE</p></div>
            <span className="text-xl text-gray-300">→</span>
            <div className="text-center"><ScoreRing value={rec.scoreAfter} size={74} stroke={7} /><p className="text-[10px] font-bold text-gray-400 mt-1">NOW</p></div>
            {rec.scoreAfter > rec.scoreBefore && <span className="text-sm font-black text-green-700 bg-green-50 px-2.5 py-1 rounded-xl">+{rec.scoreAfter - rec.scoreBefore}</span>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 mt-4 pt-4 border-t border-gray-100">
          <div className="flex bg-gray-100 rounded-xl p-1" role="radiogroup" aria-label="Design">
            {(Object.keys(LOOK) as AtsDesign[]).map(d => (
              <button key={d} onClick={() => pickDesign(d)} title={LOOK[d].blurb} role="radio" aria-checked={design === d}
                className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all ${design === d ? 'bg-white text-[#2b2240] shadow' : 'text-gray-500 hover:text-gray-800'}`}>
                {LOOK[d].label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none px-2">
            <span className={`relative w-9 h-5 rounded-full transition-colors ${showChanges ? 'bg-[#F0B429]' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${showChanges ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
            <input type="checkbox" className="sr-only" checked={showChanges} onChange={e => setShowChanges(e.target.checked)} />
            <span className="text-xs font-extrabold text-gray-700">Show changes</span>
          </label>
          <div className="flex-1" />
          {rec.pdfUrl && <a href={resumeFileUrl(rec.pdfUrl)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-extrabold text-white shadow-[0_6px_18px_-8px_rgba(43,34,64,.8)]" style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.purple2})` }}>⬇ PDF</a>}
          {rec.docxUrl && <a href={resumeFileUrl(rec.docxUrl)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-extrabold bg-white border border-gray-200 text-gray-700 hover:border-purple-300">⬇ Word</a>}
          <div className="relative" ref={menuRef}>
            <Btn onClick={() => setMenu(m => !m)} aria-label="More">⋯ More</Btn>
            {menu && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 p-1.5 z-30 ats-pop">
                <button className={menuItem} onClick={() => { setMenu(false); setTailoring(true); }}>↪ Use for another job</button>
                {rec.compare && <button className={menuItem} onClick={() => { setMenu(false); onCompare(); }}>⚖ See the model comparison</button>}
                <button className={menuItem} onClick={() => { setMenu(false); onRechoose(); }}>📊 See the match report again</button>
                {rec.fileUrl && <a className={menuItem} href={resumeFileUrl(rec.fileUrl)} target="_blank" rel="noopener noreferrer" onClick={() => setMenu(false)}>📄 Original resume file</a>}
              </div>
            )}
          </div>
        </div>
        {showChanges && (
          <p className="text-[11px] text-gray-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <span><mark className="bg-yellow-200 rounded px-1">new words</mark></span>
            <span><del className="text-red-400">removed words</del></span>
            <span><mark className="bg-green-100 rounded px-1">new line</mark></span>
            <span className="text-gray-400">Click any line to edit it. The files never show these colours.</span>
          </p>
        )}
      </div>

      {/* Phone: one pane at a time */}
      <div className="lg:hidden sticky top-0 z-20 flex bg-white rounded-2xl border border-gray-100 p-1 ats-card">
        {([['resume', '📄 Resume'], ['coach', `🤖 Coach${openTips ? ` · ${openTips}` : ''}`]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setPane(k)} className={`flex-1 py-2 rounded-xl text-xs font-extrabold ${pane === k ? 'text-white' : 'text-gray-500'}`}
            style={pane === k ? { background: `linear-gradient(135deg, ${C.purple}, ${C.purple2})` } : {}}>{label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_420px] gap-5 items-start">
        <div className={`${pane === 'resume' ? '' : 'hidden'} lg:block min-w-0`}>
          <div className="rounded-3xl p-2.5 sm:p-6" style={{ background: 'linear-gradient(180deg,#efedf5,#f6f5f9)' }}>
            <ResumePaper doc={draft} design={design} showChanges={showChanges} readOnly={aiBusy} onChange={setDraft} />
          </div>
          {dirty && (
            <div className="sticky bottom-0 z-20 mt-3">
              <div className="rounded-2xl bg-[#2b2240] text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 shadow-2xl ats-pop">
                <p className="text-xs flex-1">✏️ You have unsaved changes. The PDF and Word files show the last saved version.</p>
                <div className="flex gap-2">
                  <button onClick={discard} disabled={saving} className="px-4 py-2 rounded-xl text-xs font-extrabold bg-white/10 hover:bg-white/20">Discard</button>
                  <Btn kind="gold" onClick={save} disabled={saving}>{saving ? 'Saving…' : '💾 Save changes'}</Btn>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={`${pane === 'coach' ? '' : 'hidden'} lg:block lg:sticky lg:top-0`}>
          <CoachPanel rec={rec} pw={pw} dirty={dirty} onChanged={onChanged} />
        </div>
      </div>

      <TailorModal open={tailoring} onClose={() => setTailoring(false)} rec={rec} pw={pw} jobs={jobs} onOpen={onOpen} />
    </div>
  );
};

export default Workspace;
